<script setup>
import { computed, ref } from 'vue'
import { Link, usePage, router } from '@inertiajs/vue3'
import { PhSignOut, PhBell, PhUser, PhList, PhX } from '@phosphor-icons/vue'
import logo from '@/../images/logo.jpg'
import AppLoader from '@pages/Shared/AppLoader.vue'

const page      = usePage()
const applicant = computed(() => page.props?.auth?.applicant || {})
const flash     = computed(() => page.props?.flash || null)

const mobileOpen = ref(false)

const logout = () => {
    router.post('/logout')
}

const navLinks = [
    { label: 'Dashboard',   href: '/applicant/dashboard' },
    { label: 'Application', href: '/applicant/application' },
    { label: 'Payments',    href: '/applicant/payments' },
]
</script>

<template>
    <div class="min-h-screen flex flex-col bg-background">

        <!-- Top Navigation -->
        <header class="sticky top-0 z-50 bg-white border-b border-gray-200 ">
            <div class="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">

                <!-- Logo -->
                <Link href="/applicant/dashboard" class="flex items-center gap-3 shrink-0">
                    <img :src="logo" class="w-9 h-9 rounded-lg object-cover shadow" alt="Logo" />
                    <div class="block">
                        <p class="text-sm font-bold text-primary leading-tight">Bn Mas'ud College</p>
                        <p class="text-[10px] text-gray-400">Applicant Portal</p>
                    </div>
                </Link>

                <!-- Desktop nav -->
                <nav class="hidden md:flex items-center gap-1">
                    <Link
                        v-for="link in navLinks"
                        :key="link.href"
                        :href="link.href"
                        class="px-4 py-2 rounded-lg text-sm font-medium transition"
                        :class="$page.url.startsWith(link.href)
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'"
                    >
                        {{ link.label }}
                    </Link>
                </nav>

                <!-- Right: user + logout -->
                <div class="flex items-center gap-2">
                    <div class="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
                        <div class="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                            <PhUser :size="14" class="text-primary" />
                        </div>
                        <span class="text-sm font-medium text-gray-700">
                            {{ applicant.first_name }} {{ applicant.last_name }}
                        </span>
                    </div>

                    <button
                        @click="logout"
                        class="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
                               text-red-500 hover:bg-red-50 transition"
                        title="Sign out"
                    >
                        <PhSignOut :size="16" />
                        <span class="hidden sm:inline">Sign Out</span>
                    </button>

                    <!-- Mobile menu toggle -->
                    <button
                        @click="mobileOpen = !mobileOpen"
                        class="md:hidden p-2 rounded-lg hover:bg-gray-100 transition"
                    >
                        <PhList v-if="!mobileOpen" :size="20" />
                        <PhX    v-else              :size="20" />
                    </button>
                </div>
            </div>

            <!-- Mobile nav dropdown -->
            <transition name="fade-slide">
                <div v-if="mobileOpen" class="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
                    <Link
                        v-for="link in navLinks"
                        :key="link.href"
                        :href="link.href"
                        @click="mobileOpen = false"
                        class="block px-4 py-2.5 rounded-lg text-sm font-medium transition"
                        :class="$page.url.startsWith(link.href)
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-600 hover:bg-gray-100'"
                    >
                        {{ link.label }}
                    </Link>
                </div>
            </transition>
        </header>

        <!-- Page content -->
        <main class="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-8">
            <slot />
        </main>

        <!-- Footer -->
        <footer class="mt-auto bg-white border-t border-gray-200 py-5 text-center text-xs text-gray-500">
            © {{ new Date().getFullYear() }} Bn Mas'ud College. All rights reserved.
        </footer>

        <AppLoader />
    </div>
</template>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active { transition: all 0.2s ease; }
.fade-slide-enter-from,
.fade-slide-leave-to    { opacity: 0; transform: translateY(-6px); }
</style>